import { EyeSlashIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { resolveWorkflowIcon } from './resolveWorkflowIcon';

import { StatusBadge } from '@/components/ui/status-badge';
import { ITEM_META, ITEM_TITLE, META_SEPARATOR, ROW_HOVER } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import type { WorkflowTemplate } from '@/services/data/CampaignService';

interface WorkflowTemplateRowProps {
  template: WorkflowTemplate;
  selected: boolean;
  /** When set, the row is non-interactive and shows a "Connect …" badge. */
  gatedReason?: string;
  onGatedAction?: () => void;
  onSelect: (id: string) => void;
  /** NEW1 — when provided, renders a hover-revealed "hide from library" affordance. */
  onHide?: (id: string) => void;
}

export const WorkflowTemplateRow: React.FC<WorkflowTemplateRowProps> = ({
  template,
  selected,
  gatedReason,
  onGatedAction,
  onSelect,
  onHide,
}) => {
  const stepCount = template.steps?.length ?? 0;
  const reviewCount = (template.steps ?? []).filter((s) => s.requires_approval).length;
  const isGated = Boolean(gatedReason);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (isGated) onGatedAction?.();
        else onSelect(template.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isGated) onGatedAction?.();
          else onSelect(template.id);
        }
      }}
      aria-pressed={selected}
      aria-disabled={isGated}
      className={cn(
        'group relative flex w-full items-start gap-3 px-5 py-3 text-left transition-colors cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/30 focus-visible:ring-inset',
        selected ? 'bg-muted/40' : ROW_HOVER,
        isGated && 'opacity-60',
      )}
    >
      {(() => {
        const WorkflowIcon = resolveWorkflowIcon(template.metadata?.icon_id);
        return (
          <WorkflowIcon
            weight="regular"
            className="mt-0.5 size-4 shrink-0 text-muted-foreground/60"
          />
        );
      })()}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className={cn(ITEM_TITLE, 'truncate', isGated && 'text-muted-foreground/50')}>
            {template.name}
          </span>
          {isGated && (
            <StatusBadge tone="warning" className="shrink-0">
              {gatedReason}
            </StatusBadge>
          )}
        </div>
        <div className={cn('mt-0.5 flex items-center gap-1.5', ITEM_META)}>
          <span>
            {stepCount} {stepCount === 1 ? 'step' : 'steps'}
          </span>
          {reviewCount > 0 && (
            <>
              <span className={META_SEPARATOR}>·</span>
              <span>
                {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
              </span>
            </>
          )}
        </div>
      </div>
      {onHide && !isGated && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onHide(template.id);
          }}
          aria-label={`Hide ${template.name} from your library`}
          className={cn(
            'inline-flex size-7 shrink-0 items-center justify-center rounded-md',
            'text-muted-foreground/60 hover:bg-muted/60 hover:text-foreground',
            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/30',
          )}
        >
          <EyeSlashIcon className="size-3.5" weight="regular" />
        </button>
      )}
    </div>
  );
};
